import { Account } from "@mezo/api"
import { ErrorMessage } from "@mezo/sdk"
import PostHog from "posthog-js"

import { SdkSigners } from "#/shared/services/sdk"
import { AccountCreationStatus, OnboardingFlow } from "#/shared/types"

import createDappAsyncThunk from "../asyncThunk"
import {
  resetAccount,
  setAccountCreationStatus,
  setContactIdentifer,
  setHasModifiedUsername,
  setUserAuthId,
  setUsername,
} from "../slices/account"
import { resetBorrowState } from "../slices/borrow"
import { resetPortal } from "../slices/portal"
import { resetProductsState } from "../slices/store"
import {
  resetUI,
  setCanSendTransactions,
  setHasAccess,
  setHasLoadedAccountData,
  setHasLoadedPartnerData,
  setNotification,
  setPartner,
} from "../slices/ui"
import {
  resetWallet,
  setConnectedChainId,
  setConnectedWallet,
} from "../slices/wallet"

import {
  fetchAllBorrowData,
  fetchMusdCollateralPrice,
  fetchMusdLiquityProps,
} from "./borrow"
import { fetchUserLeaderboardRank, refreshLeaderboard } from "./leaderboard"
import { fetchMats } from "./mats"
import { fetchUserMatsnetLeaderboardRank } from "./matsnet"
import { updateBalances } from "./portal"
import { fetchAccountReferralCodeData } from "./referrals"
import { createSession } from "./session"
import { getCollectedProducts } from "./store"

export const addContactIdentifier = createDappAsyncThunk(
  "account/addContactIdentifier",
  async (
    {
      contactIdentifier,
      identifierType,
    }: { address: string; contactIdentifier: string; identifierType: string },
    { extra: { sdk } },
  ): Promise<boolean> =>
    sdk.mezo.api.account.addContactIdentifier(
      contactIdentifier,
      identifierType,
    ),
)

export const fetchContactIdentifier = createDappAsyncThunk(
  "account/fetchContactIdentifier",
  async (_, { dispatch, extra: { sdk } }) => {
    const identifiers = await sdk.mezo.api.account.getContactIdentifier()

    const email =
      identifiers.find((identifier) => identifier.identifierType === "email")
        ?.contactIdentifier ?? null

    const telegram =
      identifiers.find((identifier) => identifier.identifierType === "telegram")
        ?.contactIdentifier ?? null

    dispatch(setContactIdentifer({ email, telegram }))
  },
)

// Don't use this directly, use loginAndFetchAccount instead
export const loginAccount = createDappAsyncThunk(
  "account/loginAccount",
  async (_, { getState, dispatch, extra: { sdk } }) => {
    const { isConnected } = getState().wallet
    const { currentReferralCode } = getState().referrals

    // TODO: canSendTransactions and hasAccess shoudl be refactored, maybe merged
    dispatch(setCanSendTransactions(false))
    dispatch(setHasLoadedAccountData(false))

    if (!isConnected) {
      dispatch(setHasLoadedAccountData(true))
      return null
    }

    const account = await sdk.mezo.api.account.getAccount()

    // If current invite code is different than used code it means that account has already
    // been created using different invite code
    if (
      account?.codeUsedByAccount &&
      currentReferralCode &&
      account?.codeUsedByAccount !== currentReferralCode
    ) {
      dispatch(setNotification("used-code"))
    }

    /// If account was created already and we have a cookie => we have access
    if (account) {
      // Identify user by Supabase Auth ID
      PostHog.identify(account.authId)

      dispatch(setUserAuthId(account.authId))
      dispatch(setUsername(account.username))
      dispatch(setHasModifiedUsername(account.hasModifiedUsername))
      dispatch(setCanSendTransactions(true))
      dispatch(setHasAccess(true))
      dispatch(setAccountCreationStatus(AccountCreationStatus.Created))
      dispatch(setHasLoadedAccountData(true))
      await dispatch(fetchContactIdentifier())

      return account
    }

    // If backend throws error the account may be there but the cookie may be lost
    // try to restore the session
    const { payload: sessionExists } = await dispatch(createSession())

    // Session was restored, we have a valid cookie
    if (sessionExists) {
      // Try to restore the account
      const restoredAccount = await sdk.mezo.api.account.getAccount()

      dispatch(setCanSendTransactions(!!restoredAccount))
      dispatch(setHasAccess(!!restoredAccount))

      if (restoredAccount !== null) {
        // Identify user by Supabase Auth ID
        PostHog.identify(restoredAccount.authId)

        dispatch(setUserAuthId(restoredAccount.authId))
        dispatch(setUsername(restoredAccount.username))
        dispatch(setHasModifiedUsername(restoredAccount.hasModifiedUsername))
      }

      dispatch(setHasLoadedAccountData(true))

      return restoredAccount
    }

    dispatch(setCanSendTransactions(false))
    dispatch(setHasAccess(false))
    dispatch(setHasModifiedUsername(false))
    dispatch(setHasLoadedAccountData(true))

    return null
  },
)

// This should be used as an endpoint
export const loginAndFetchAccount = createDappAsyncThunk(
  "account/loginAndFetchAccount",
  async (_, { dispatch }) => {
    const { payload: account } = await dispatch(loginAccount())

    if (account) {
      await dispatch(fetchMats())
      await dispatch(fetchUserLeaderboardRank())
      await dispatch(fetchUserMatsnetLeaderboardRank())
      await dispatch(fetchAccountReferralCodeData())
    }

    return account
  },
)

// This should be used as an endpoint
export const createAccount = createDappAsyncThunk(
  "account/createAccount",
  async (
    { code }: { code?: string },
    { dispatch, extra: { sdk } },
  ): Promise<Account | ErrorMessage> => {
    const { payload: session } = await dispatch(createSession())

    if (!session) {
      return { hasError: true, message: "No session. Account not created" }
    }

    const account = await sdk.mezo.api.account.createAccount(code)

    if (!("hasError" in account)) {
      dispatch(setNotification("account-created"))
      dispatch(loginAndFetchAccount())
    }

    return account
  },
)

export const updateAccount = createDappAsyncThunk(
  "account/updateAccount",
  async (username: string, { getState, dispatch, extra: { sdk } }) => {
    const { isConnected } = getState().wallet

    if (!isConnected) return false

    const account = (await sdk.mezo.api.account.updateAccount(
      username,
    )) as Account | null

    if (!account) {
      dispatch(setHasModifiedUsername(false))
      return false
    }

    dispatch(setUsername(account.username))
    dispatch(setHasModifiedUsername(account.hasModifiedUsername))
    dispatch(refreshLeaderboard())

    return true
  },
)

export const connectWalletAccount = createDappAsyncThunk(
  "account/connectWalletAccount",
  async (
    {
      name,
      signerEthersV5,
      signerEthersV6,
      signerMezo,
      isBitcoinSigner,
    }: {
      name?: string | null
    } & SdkSigners,
    { dispatch, extra: { sdk } },
  ) => {
    await sdk.connectSigner({
      signerEthersV5,
      signerEthersV6,
      signerMezo,
      isBitcoinSigner,
    })

    if (!sdk.mezo.wallet) {
      throw new Error("Wallet should be already connected.")
    }

    const { evmAddress, btcAddress } = sdk.mezo.wallet

    dispatch(setConnectedWallet({ evmAddress, btcAddress, name }))
  },
)

export const updateAccountCreationStatus = createDappAsyncThunk(
  "account/updateAccountCreationStatus",
  async (
    {
      chainId,
      accountExists,
    }: { chainId: number | null; accountExists: boolean },
    { getState, dispatch },
  ) => {
    const { flow: onboardingFlow } = getState().onboarding

    if (onboardingFlow === OnboardingFlow.SIGN_UP && !accountExists) {
      // If we are in Sign up flow we only need wallet connection
      // at this point
      dispatch(setAccountCreationStatus(AccountCreationStatus.Connected))
    } else {
      // If we are in Sign in flow we need to prompt user to sign
      // message to create session
      dispatch(setAccountCreationStatus(AccountCreationStatus.Signing))
    }

    dispatch(setConnectedChainId(chainId))
  },
)

export const fetchWalletAccount = createDappAsyncThunk(
  "account/fetchWalletAccount",
  async (_, { dispatch }) => {
    // Portal
    await dispatch(updateBalances({ fetchOnChainData: true }))
    await dispatch(fetchUserMatsnetLeaderboardRank())
    await dispatch(fetchUserLeaderboardRank())
    await dispatch(fetchContactIdentifier())

    // MUSD
    await dispatch(fetchMusdCollateralPrice())
    await dispatch(fetchMusdLiquityProps())
    await dispatch(getCollectedProducts())
    await dispatch(fetchAllBorrowData())
  },
)

export const disconnectWalletAccount = createDappAsyncThunk(
  "account/disconnectWalletAccount",
  async (_, { dispatch, extra: { sdk } }) => {
    await sdk.disconnectSigner()
    dispatch(resetBorrowState())
    dispatch(resetWallet())
    dispatch(resetPortal())
    dispatch(resetAccount())
    dispatch(resetUI())
    dispatch(resetProductsState())
  },
)

export const checkAvaiableUsername = createDappAsyncThunk(
  "account/checkAvaiableUsername",
  async (username: string, { extra: { sdk } }) => {
    const usernameAvailable =
      await sdk.mezo.api.account.checkAvailableUsername(username)

    return usernameAvailable
  },
)

export const getPartnerAccount = createDappAsyncThunk(
  "account/getPartnerAccount",
  async (partnerSlug: string, { dispatch, extra: { sdk } }) => {
    const partnerAccount =
      await sdk.mezo.api.account.getPartnerAccount(partnerSlug)

    dispatch(setPartner(partnerAccount))
    dispatch(setHasLoadedPartnerData(true))
  },
)

export const stackMatsnetBtc = createDappAsyncThunk(
  "account/stackMatsnetBtc",
  async (
    { mats }: { mats: number },
    { dispatch, extra: { sdk } },
  ): Promise<{ success: boolean; transactionHash?: string }> => {
    const response = await sdk.mezo.api.matsnet.stackMatsnetBtc(mats)

    await dispatch(fetchMats())

    return response
  },
)
